import React, { ChangeEvent, useState } from 'react'
import axios from 'axios'
import { makeStyles } from '@material-ui/core/styles'
import { Button, Radio, RadioGroup } from '@material-ui/core'
import { DropzoneArea } from 'material-ui-dropzone/dist'
import { themeColors } from 'constants/theme/colors'
import TypographyIcon from 'images/white-label/typography.png'
import VisualStyleIcon from 'images/white-label/visual-style.png'
import ErrorMessage from '../../components/ErrorMessage'
import { OrganizationSettings } from '../../dtos/OrganizationDTO'
import CircularSpinner from '../../components/CircularSpinner'
import EditOrganizationLayout from '../../layouts/EditOrganizationLayout'
import Navbar from '../../components/Navbar'
import PalettesContainer from '../../components/palettes/PalettesContainer'
import { useOrganizationWLSettings } from '../../hooks/useOrganizationWLSettings'
import useCredentials from '../../hooks/useCredentials'
import HeaderWithIcon from '../../components/HeaderWithIcon'

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(5),
  },
  contentActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  themeCustomization: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(3),
  },
  themeCustomizationItem: {
    display: 'flex',
    width: '186px',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(1.5, 3),
    backgroundColor: theme.palette.neutrals[50],
    marginBottom: theme.spacing(4),

    '& label': {
      fontSize: 14,
      fontWeight: 700,
      color: theme.palette.neutrals[600],
    },
  },
  select: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',

    '& select': {
      width: '100%',
    },
  },
  capitalize: {
    textTransform: 'capitalize',
  },
}))

const fontFamilies = [
  'Proxima Nova',
  'Helvetica',
  'Inter',
  'Roboto',
  'Poppins',
  'IBM Plex Sans',
  'Manrope',
  'Space Grotesk',
  'Raleway',
  'DM Sans',
]
const palettes = Object.entries(themeColors).map(([key, value]) => ({
  name: key,
  colors: [value[500], value[600], value[700]],
}))

const FAVICON_MAX_SIZE = 1048576
const FAVICON_ASPECT_RATIO_TOLERANCE = 0.1

const EditOrganizationTypography: React.FC = () => {
  const classes = useStyles()
  const { credentials } = useCredentials()

  const {
    formData,
    isWhiteLabel,
    subDomain,
    isFormEdited,
    loadDataError,
    handleSubmit,
    loading,
    setFormData,
    setIsFormEdited,
    organizationId,
    loadOrganization,
    faviconUrl,
  } = useOrganizationWLSettings()

  const [favicon, setFavicon] = useState<File | 'remove' | null>(null)
  const [faviconErrors, setFaviconErrors] = useState<string[]>([])

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => {
      const copy = structuredClone(prev)
      const nameArray = e.target.name.split('.')
      const { value } = e.target

      if (!copy[nameArray[0] as keyof OrganizationSettings]) {
        copy[nameArray[0] as keyof OrganizationSettings] = {} as any
      }

      // @ts-ignore
      copy[nameArray[0] as keyof OrganizationSettings][nameArray[1]] = value as string
      console.log(copy)
      return copy
    })
    setIsFormEdited(true)
  }

  const handleThemeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => {
      const copy = structuredClone(prev)
      if (!copy.Typography) {
        copy.Typography = {} as any
      }
      if (!copy.Typography.ThemeCustomization) {
        copy.Typography.ThemeCustomization = {} as any
      }
      copy.Typography.ThemeCustomization.SelectedMode = e.target.value
      return copy
    })
    setIsFormEdited(true)
  }

  const handlePaletteChange = (value: string) => {
    setFormData((prev) => {
      const copy = structuredClone(prev)
      if (!copy.Typography) {
        copy.Typography = {} as any
      }
      copy.Typography.ColorScheme = value
      return copy
    })
    setIsFormEdited(true)
  }

  const handleCustomPaletteChange = (value: string[]) => {
    setFormData((prev) => {
      const copy = structuredClone(prev)
      if (!copy.Typography) {
        copy.Typography = {} as any
      }
      if (!copy.Typography.ThemeCustomization) {
        copy.Typography.ThemeCustomization = {} as any
      }
      if (!copy.Typography.ThemeCustomization.ColorScheme) {
        copy.Typography.ThemeCustomization.ColorScheme = {} as any
      }
      if (!copy.Typography.ThemeCustomization.ColorScheme.WhiteMode) {
        copy.Typography.ThemeCustomization.ColorScheme.WhiteMode = {} as any
      }
      copy.Typography.ThemeCustomization.ColorScheme.WhiteMode.CustomScheme = {
        PrimaryColor: value[0],
        SecondaryColor: { Color: value[1], Shades: null },
        TertiaryColor: { Color: value[2], Shades: null },
        SupportColor: null,
        SchemeName: null,
      }
      return copy
    })
    setIsFormEdited(true)
  }

  const FAVICON_NON_PROCESSABLE_TYPES = ['image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon']
  const FAVICON_NON_PROCESSABLE_EXTENSIONS = ['svg', 'ico']

  const isNonProcessableFavicon = (file: File): boolean => {
    if (FAVICON_NON_PROCESSABLE_TYPES.includes(file.type)) return true
    const ext = file.name.toLowerCase().split('.').pop() ?? ''
    return FAVICON_NON_PROCESSABLE_EXTENSIONS.includes(ext)
  }

  const handleFaviconChange = (files: File[]) => {
    const file = files[0]
    if (!file) {
      setFavicon(faviconUrl ? 'remove' : null)
      return
    }

    if (isNonProcessableFavicon(file)) {
      setFaviconErrors([])
      setFavicon(file)
      return
    }

    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      const ratio = img.width / img.height
      if (Math.abs(ratio - 1) > FAVICON_ASPECT_RATIO_TOLERANCE) {
        setFaviconErrors(['Image must be square (1:1 ratio).'])
        setFavicon(null)
      } else {
        setFaviconErrors([])
        setFavicon(file)
      }
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      setFaviconErrors(['Could not read image dimensions.'])
      setFavicon(null)
    }
    img.src = url
  }

  const handleFaviconSubmit = async () => {
    if (!favicon) return true

    const fd = new FormData()
    fd.append('favicon', favicon)
    await axios.put(`/api/panel/organizations/${organizationId}`, fd, {
      headers: credentials,
    })

    return true
  }

  const handleSave = async () => {
    const faviconSaved = await handleFaviconSubmit()
    if (!faviconSaved) return

    const settingsSaved = await handleSubmit()
    if (settingsSaved) {
      setFavicon(null)
    }
  }

  const handleCustomPaletteNameChange = (value: string) => {
    setFormData((prev) => {
      const copy = structuredClone(prev)
      if (!copy.Typography) {
        copy.Typography = {} as any
      }
      if (!copy.Typography.ThemeCustomization) {
        copy.Typography.ThemeCustomization = {} as any
      }
      if (!copy.Typography.ThemeCustomization.ColorScheme) {
        copy.Typography.ThemeCustomization.ColorScheme = {} as any
      }
      if (!copy.Typography.ThemeCustomization.ColorScheme.WhiteMode) {
        copy.Typography.ThemeCustomization.ColorScheme.WhiteMode = {} as any
      }
      if (!copy.Typography.ThemeCustomization.ColorScheme.WhiteMode.CustomScheme) {
        copy.Typography.ThemeCustomization.ColorScheme.WhiteMode.CustomScheme = {} as any
      }
      copy.Typography.ThemeCustomization.ColorScheme.WhiteMode.CustomScheme.SchemeName = value
      return copy
    })
    setIsFormEdited(true)
  }

  return (
    <div>
      <div className='hero-head'>
        <Navbar />
      </div>
      {loadDataError && <ErrorMessage text={loadDataError} />}
      {loading ? (
        <CircularSpinner className='is-center' />
      ) : (
        <EditOrganizationLayout
          organizationId={organizationId}
          reloadOrganization={loadOrganization}
          isWhiteLabel={isWhiteLabel}
          subDomain={subDomain}
          isFormEdited={isFormEdited}
          isWhiteLabelPage
        >
          <div className={classes.content}>
            <div className={classes.contentActions}>
              <HeaderWithIcon
                iconUrl={TypographyIcon}
                title='Typography'
                description='Customize the look and feel of your platform'
              />
              <Button color='primary' variant='contained' onClick={handleSave}>
                Save
              </Button>
            </div>
            <div className='columns is-justify-content-center is-0'>
              <div className='column p-0'>
                <div className='columns is-flex mb-6'>
                  <div className={`column field ${classes.select}`}>
                    <label>Header Font Family</label>
                    <div className='select'>
                      <select
                        value={formData.Typography?.HeaderFontFamily || 'Proxima Nova'}
                        onChange={handleChange}
                        name='Typography.HeaderFontFamily'
                      >
                        {fontFamilies.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className={`column field ${classes.select}`}>
                    <label>Body Font Family</label>
                    <div className='select'>
                      <select
                        value={formData.Typography?.BodyFontFamily || 'Proxima Nova'}
                        onChange={handleChange}
                        name='Typography.BodyFontFamily'
                      >
                        {fontFamilies.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className='columns is-flex mb-6'>
                  <div className={`column field ${classes.select}`}>
                    <label>Button Corner Radius</label>
                    <div className='select'>
                      <select
                        value={formData.Typography?.ButtonCornerRadius || '0'}
                        onChange={handleChange}
                        name='Typography.ButtonCornerRadius'
                      >
                        {['0', '2', '4', '8', '16', '24', '32'].map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className={`column field ${classes.select}`}>
                    <label>Button Text color</label>
                    <div className='select'>
                      <select
                        value={formData.Typography?.ButtonTextColor || 'white'}
                        onChange={handleChange}
                        name='Typography.ButtonTextColor'
                        disabled={formData.Typography?.ColorScheme !== 'custom'}
                        title='Only available when custom color scheme is selected'
                      >
                        {['white', 'black'].map((name) => (
                          <option key={name} value={name} className={classes.capitalize}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                <div className='mb-4'>
                  <HeaderWithIcon
                    iconUrl={VisualStyleIcon}
                    title='Visual Style'
                    description='Customize the look and feel of your platform'
                    className='mb-4'
                  />
                  <PalettesContainer
                    palettes={palettes}
                    onSelect={handlePaletteChange}
                    selectedPalette={formData.Typography?.ColorScheme || 'primary'}
                    customPalette={formData.Typography?.ThemeCustomization?.ColorScheme?.WhiteMode?.CustomScheme}
                    onCustomPaletteChange={handleCustomPaletteChange}
                    onCustomPaletteNameChange={handleCustomPaletteNameChange}
                  />
                </div>
                <div className='mb-4'>
                  <HeaderWithIcon
                    className='mb-4 mt-4'
                    iconUrl={VisualStyleIcon}
                    title='Theme customization'
                    description='This feature only changes the color of the header and footer.'
                  />
                  <RadioGroup className={classes.themeCustomization} onChange={handleThemeChange}>
                    <div className={classes.themeCustomizationItem}>
                      <label>White Mode</label>
                      <Radio
                        name='Typography.ThemeCustomization.SelectedMode'
                        value='WhiteMode'
                        color='primary'
                        checked={formData.Typography?.ThemeCustomization?.SelectedMode === 'WhiteMode'}
                      />
                    </div>
                    <div className={classes.themeCustomizationItem}>
                      <label>Dark Mode</label>
                      <Radio
                        name='Typography.ThemeCustomization.SelectedMode'
                        value='DarkMode'
                        color='primary'
                        checked={
                          formData.Typography?.ThemeCustomization?.SelectedMode === 'DarkMode' ||
                          formData.Typography?.ThemeCustomization?.SelectedMode === undefined
                        }
                      />
                    </div>
                  </RadioGroup>
                </div>
                <div className='columns mb-6'>
                  <div className='field column'>
                    <label htmlFor='favicon' className='label'>
                      Favicon
                      <div className='control'>
                        <DropzoneArea
                          key={faviconUrl || 'no-favicon'}
                          showAlerts={false}
                          filesLimit={1}
                          maxFileSize={FAVICON_MAX_SIZE}
                          acceptedFiles={['image/png', 'image/x-icon', 'image/vnd.microsoft.icon']}
                          initialFiles={faviconUrl ? [faviconUrl] : undefined}
                          onChange={handleFaviconChange}
                          onDropRejected={() => {
                            setFaviconErrors(['File rejected. Must be PNG or ICO under 1 MB.'])
                          }}
                          onDrop={() => {
                            setFaviconErrors([])
                          }}
                        />
                      </div>
                    </label>
                    <p className='help'>Accepts PNG or ICO. Must be square (1:1 ratio).</p>
                    {faviconErrors.map((message) => (
                      <p className='help is-danger' key={message}>
                        {message}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </EditOrganizationLayout>
      )}
    </div>
  )
}

export default EditOrganizationTypography
